<template>
  <main class="asocoins-admin">
    <div class="asocoins-admin__inner">
      <header class="asocoins-admin__header">
        <div>
          <p class="asocoins-overline">ASOFOM</p>
          <h1>Administración de Asocoins</h1>
          <p class="asocoins-admin__description">Reglas, carteras y asignaciones con historial auditable.</p>
        </div>
        <div class="asocoins-admin__header-actions">
          <router-link class="asocoins-button asocoins-button--outline" :to="{ name: 'myCommunity' }">
            Mis comunicados
          </router-link>
          <button class="asocoins-button asocoins-button--outline" type="button" :disabled="loading" @click="refresh">
            <span aria-hidden="true">↻</span> Actualizar
          </button>
        </div>
      </header>

      <div v-if="error" class="asocoins-alert asocoins-alert--error" role="alert">{{ error }}</div>

      <nav class="asocoins-admin__tabs" aria-label="Secciones de administración de Asocoins">
        <button
          class="asocoins-admin__tab"
          :class="{ 'asocoins-admin__tab--active': adminSection === 'wallets' }"
          type="button"
          :aria-selected="adminSection === 'wallets'"
          role="tab"
          @click="adminSection = 'wallets'"
        >
          <span class="asocoins-admin__tab-icon" aria-hidden="true">◈</span>
          <span><strong>Carteras</strong><small>SOFOMES, saldos y movimientos</small></span>
        </button>
        <button
          class="asocoins-admin__tab"
          :class="{ 'asocoins-admin__tab--active': adminSection === 'rules' }"
          type="button"
          :aria-selected="adminSection === 'rules'"
          role="tab"
          @click="adminSection = 'rules'"
        >
          <span class="asocoins-admin__tab-icon" aria-hidden="true">≡</span>
          <span><strong>Reglas</strong><small>Catálogo y estado operativo</small></span>
        </button>
      </nav>

      <section v-if="adminSection === 'wallets'" class="asocoins-admin__section" role="tabpanel">
        <header class="asocoins-admin__section-header">
          <div>
            <p class="asocoins-overline">Gestión de saldo</p>
            <h2>Carteras de SOFOMES</h2>
            <p class="asocoins-admin__section-description">Selecciona una SOFOM para revisar su saldo, consultar movimientos o registrar un ajuste manual.</p>
          </div>
          <span v-if="selectedSofom" class="asocoins-context-badge">{{ selectedSofom.company || selectedSofom.name || selectedSofom.email }}</span>
        </header>

        <div class="asocoins-grid asocoins-grid--admin">
          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>SOFOMES de ASOFOM</h2></div>
            <div class="asocoins-card__body">
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-sofom-search">Buscar</label>
                <input id="asocoins-sofom-search" v-model="search" class="asocoins-input" type="search" placeholder="Nombre, correo o empresa" @input="loadSofomes" />
              </div>
              <ul class="asocoins-list asocoins-list--sofomes">
                <li v-for="sofom in sofomes.docs" :key="sofom._id">
                  <button
                    class="asocoins-sofom-option"
                    :class="{ 'asocoins-sofom-option--selected': selectedSofomId === sofom._id }"
                    type="button"
                    @click="selectSofom(sofom)"
                  >
                    <span>
                      <span class="asocoins-sofom-option__name">{{ sofom.company || sofom.name || sofom.email }}</span>
                      <span class="asocoins-sofom-option__meta">{{ sofom.email }} · {{ formatPoints(sofom.availableBalance) }} Asocoins</span>
                    </span>
                    <span class="asocoins-sofom-option__arrow" aria-hidden="true">›</span>
                  </button>
                </li>
                <li v-if="!sofomes.docs.length" class="asocoins-empty">No hay resultados.</li>
              </ul>
            </div>
          </article>

          <article class="asocoins-card asocoins-card--account">
            <div class="asocoins-card__header">
              <h2>{{ selectedSofom ? (selectedSofom.company || selectedSofom.name) : "Detalle de cartera" }}</h2>
            </div>
            <div class="asocoins-card__body">
              <div v-if="!selectedSofomId" class="asocoins-alert asocoins-alert--info">Selecciona una SOFOM para consultar su cartera.</div>
              <div v-else class="asocoins-admin__wallet">
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">Saldo disponible</p>
                  <p class="asocoins-stat__value">{{ formatPoints(adminWallet && adminWallet.availableBalance) }}</p>
                </div>
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">Equivalencia</p>
                  <p class="asocoins-stat__value asocoins-stat__value--small">{{ formatMoney((adminWallet && adminWallet.availableBalance) / 10) }} MXN</p>
                </div>
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">ID del miembro SOFOM</p>
                  <p class="asocoins-stat__value asocoins-stat__value--id">{{ selectedSofomId }}</p>
                </div>
              </div>
            </div>
          </article>
        </div>

        <template v-if="selectedSofomId">
        <div class="asocoins-grid asocoins-grid--admin-lower">
          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>Asignación manual</h2></div>
            <div class="asocoins-card__body">
              <form class="asocoins-form" @submit.prevent="assign">
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-rule">Ítem del catálogo</label>
                  <select id="asocoins-assignment-rule" v-model="assignment.ruleId" class="asocoins-select" required @change="handleAssignmentRuleChange">
                    <option :value="null" disabled>Selecciona una regla</option>
                    <option v-for="rule in rules" :key="rule._id" :value="rule._id">{{ rule.name }} · {{ formatRuleAmount(rule) }} Asocoins</option>
                  </select>
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-quantity">Cantidad</label>
                  <input id="asocoins-assignment-quantity" v-model.number="assignment.quantity" class="asocoins-input" type="number" min="1" />
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-amount">Importe aplicado</label>
                  <input id="asocoins-assignment-amount" v-model.number="assignment.amount" class="asocoins-input" type="number" min="0" />
                  <p class="asocoins-field__hint">Se precarga con el importe de la regla y puedes ajustarlo antes de aplicar.</p>
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-note">Nota</label>
                  <textarea id="asocoins-assignment-note" v-model="assignment.note" class="asocoins-textarea" rows="2"></textarea>
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-reference">Referencia de validación</label>
                  <input id="asocoins-assignment-reference" v-model="assignment.reference" class="asocoins-input" type="text" />
                </div>
                <div class="asocoins-actions">
                  <button class="asocoins-button asocoins-button--outline" type="button" :disabled="previewLoading" @click="preview">
                    {{ previewLoading ? "Calculando…" : "Vista previa" }}
                  </button>
                  <button class="asocoins-button asocoins-button--amber" type="submit" :disabled="loading">
                    {{ loading ? "Aplicando…" : "Aplicar asignación" }}
                  </button>
                </div>
              </form>
              <div v-if="previewData" class="asocoins-alert asocoins-alert--info">
                {{ formatPoints(previewData.amount) }} Asocoins · {{ previewData.direction === 'DEBIT' ? 'débito' : 'abono' }}
              </div>
            </div>
          </article>

          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>Resumen de operación</h2></div>
            <div class="asocoins-card__body">
              <p class="asocoins-body-copy">La asignación se registra siempre contra la cartera de la SOFOM seleccionada. Usa una vista previa antes de confirmar ajustes manuales.</p>
              <dl class="asocoins-operation-summary">
                <div><dt>SOFOM seleccionada</dt><dd>{{ selectedSofom ? (selectedSofom.company || selectedSofom.name || selectedSofom.email) : "Ninguna" }}</dd></div>
                <div><dt>Regla seleccionada</dt><dd>{{ selectedRule ? selectedRule.name : "Ninguna" }}</dd></div>
              </dl>
            </div>
          </article>
        </div>

        <article class="asocoins-card">
          <div class="asocoins-card__header">
            <div>
              <h2>Estado de cuenta</h2>
              <span class="asocoins-caption">Movimientos de la SOFOM seleccionada</span>
            </div>
          </div>
          <div class="asocoins-card__body">
            <div class="asocoins-filters">
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-type">Tipo</label>
                <select id="asocoins-filter-type" v-model="movementFilters.type" class="asocoins-select" @change="reloadMovements">
                  <option :value="null">Todos</option>
                  <option v-for="type in movementTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-source">Origen</label>
                <input id="asocoins-filter-source" v-model="movementFilters.source" class="asocoins-input" type="text" @change="reloadMovements" />
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-user">Usuario / actor</label>
                <input id="asocoins-filter-user" v-model="movementFilters.userId" class="asocoins-input" type="text" @change="reloadMovements" />
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-start">Desde</label>
                <input id="asocoins-filter-start" v-model="movementFilters.startDate" class="asocoins-input" type="date" @change="reloadMovements" />
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-end">Hasta</label>
                <input id="asocoins-filter-end" v-model="movementFilters.endDate" class="asocoins-input" type="date" @change="reloadMovements" />
              </div>
              <button class="asocoins-button asocoins-button--ghost" type="button" @click="clearMovementFilters">Limpiar filtros</button>
            </div>
          </div>
          <div class="asocoins-table-wrap">
            <table class="asocoins-table">
              <thead><tr><th>Fecha</th><th>Concepto</th><th>Origen</th><th class="is-number">Importe</th><th class="is-number">Saldo</th></tr></thead>
              <tbody>
                <tr v-for="movement in adminMovements.docs" :key="movement._id">
                  <td>{{ formatDate(movement.createdAt) }}</td>
                  <td>{{ movement.concept || "—" }}</td>
                  <td>{{ movement.source || "—" }}</td>
                  <td class="is-number" :class="movement.direction === 'DEBIT' ? 'is-debit' : 'is-credit'">{{ movement.direction === 'DEBIT' ? '−' : '+' }}{{ formatPoints(movement.amount) }}</td>
                  <td class="is-number">{{ formatPoints(movement.balanceAfter) }}</td>
                </tr>
                <tr v-if="!adminMovements.docs.length"><td colspan="5" class="asocoins-empty">No hay movimientos.</td></tr>
              </tbody>
            </table>
          </div>
        </article>
        </template>
        <div v-else class="asocoins-selection-hint">Selecciona una SOFOM para habilitar la asignación manual y el estado de cuenta.</div>
      </section>

      <section v-else class="asocoins-admin__section" role="tabpanel">
        <header class="asocoins-admin__section-header">
          <div>
            <p class="asocoins-overline">Configuración auditable</p>
            <h2>Catálogo de reglas</h2>
            <p class="asocoins-admin__section-description">Consulta los importes y el estado de cada regla que alimenta las recompensas de Asocoins.</p>
          </div>
          <div class="asocoins-rules-note"><span class="asocoins-status asocoins-status--active">Activo</span> Las reglas activas pueden utilizarse en eventos y encuestas.</div>
        </header>
        <article class="asocoins-card">
          <div class="asocoins-table-wrap">
            <table class="asocoins-table">
              <thead><tr><th>Concepto</th><th>Categoría</th><th>Importe</th><th>Estado</th></tr></thead>
              <tbody>
                <tr v-for="rule in rules" :key="rule._id">
                  <td><strong>{{ rule.name }}</strong></td>
                  <td>{{ rule.category }}</td>
                  <td>{{ formatPoints(rule.amount) }} Asocoins</td>
                  <td><span class="asocoins-status" :class="{ 'asocoins-status--active': rule.active }">{{ rule.active ? 'Activa' : 'Inactiva' }}</span></td>
                </tr>
                <tr v-if="!rules.length"><td colspan="4" class="asocoins-empty">No hay reglas disponibles.</td></tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </div>
  </main>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "AsocoinsAdmin",
  data() {
    return {
      search: "",
      adminSection: "wallets",
      selectedSofomId: null,
      selectedSofom: null,
      previewLoading: false,
      previewData: null,
      assignment: { ruleId: null, quantity: 1, amount: null, note: "", reference: "" },
      movementFilters: { type: null, source: "", userId: "", startDate: "", endDate: "" },
      movementTypes: ["CREDIT", "DEBIT", "PENALTY", "EXPIRATION", "ADJUSTMENT"],
    };
  },
  computed: {
    ...mapState("asocoins", ["sofomes", "rules", "adminWallet", "adminMovements", "loading", "error"]),
    selectedRule() {
      return this.rules.find((rule) => rule._id === this.assignment.ruleId) || null;
    },
  },
  methods: {
    ...mapActions("asocoins", ["getAdminSofomes", "getAdminRules", "getAdminWallet", "getAdminMovements", "assignAsocoins"]),
    async refresh() {
      await Promise.all([this.loadSofomes(), this.getAdminRules()]);
      if (this.selectedSofomId) await this.loadAccount(this.selectedSofom);
    },
    loadSofomes() {
      return this.getAdminSofomes({ search: this.search, page: 1, limit: 50 });
    },
    async selectSofom(sofom) {
      this.selectedSofom = sofom;
      this.selectedSofomId = sofom._id;
      this.previewData = null;
      await this.loadAccount(sofom);
    },
    async loadAccount(sofom) {
      if (!sofom || !sofom._id) return;
      await Promise.all([
        this.getAdminWallet(sofom._id),
        this.getAdminMovements({ sofomId: sofom._id, params: { page: 1, limit: 50, ...this.movementFilters } }),
      ]);
    },
    reloadMovements() {
      return this.loadAccount(this.selectedSofom);
    },
    clearMovementFilters() {
      this.movementFilters = { type: null, source: "", userId: "", startDate: "", endDate: "" };
      return this.reloadMovements();
    },
    handleAssignmentRuleChange() {
      const rule = this.selectedRule;
      this.assignment.amount = rule ? this.ruleDefaultAmount(rule) : null;
      this.previewData = null;
    },
    async preview() {
      if (!this.selectedSofomId || !this.assignment.ruleId) return;
      this.previewLoading = true;
      const payload = { ...this.assignment, dryRun: true };
      if (payload.amount === null || payload.amount === "") delete payload.amount;
      const result = await this.assignAsocoins({ sofomId: this.selectedSofomId, payload });
      this.previewData = result.status ? result.data.preview : null;
      this.previewLoading = false;
    },
    async assign() {
      if (!this.selectedSofomId || !this.assignment.ruleId) return;
      const payload = { ...this.assignment };
      if (payload.amount === null || payload.amount === "") delete payload.amount;
      const result = await this.assignAsocoins({ sofomId: this.selectedSofomId, payload });
      if (result.status) {
        this.$store.commit("notification/show", { color: "success", text: "Asignación registrada." });
        const ruleId = this.assignment.ruleId;
        const rule = this.rules.find((item) => item._id === ruleId);
        this.assignment = { ruleId, quantity: 1, amount: rule ? this.ruleDefaultAmount(rule) : null, note: "", reference: "" };
        await this.refresh();
      } else {
        this.$store.commit("notification/show", { color: "error", text: result.message });
      }
    },
    ruleDefaultAmount(rule) {
      return rule && rule.amount !== undefined && rule.amount !== null ? Number(rule.amount) : null;
    },
    formatRuleAmount(rule) {
      return this.formatPoints(this.ruleDefaultAmount(rule));
    },
    formatPoints(value) { return new Intl.NumberFormat("es-MX").format(Number(value) || 0); },
    formatMoney(value) { return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(value) || 0); },
    formatDate(value) { return value ? new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—"; },
  },
  created() { this.refresh(); },
};
</script>
